import { useEffect, useState } from 'react';
import { Card, Stack, Group, Text, Button, Title, SimpleGrid, Badge } from '@repletoryreach/sdk-ui';
import { NumberInput, Switch } from '@repletoryreach/sdk-ui/inputs';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useFxRates, useUpdateFxRate, useRefreshFxRates, useUpdateFxSettings } from '../../../../data/useSkuRates';
import { formatDateTime } from '../../../../lib/format';
import { useT } from '../../../../i18n/I18nProvider';

const GRID_COLS = { base: 1, sm: 2, lg: 3 };

/** What one unit of a currency costs in rand at a rate quoted per rand: 0.0615 dollars per rand is R16.26 a dollar. */
function randEach(perZar: number | undefined | null): string {
  if (!perZar || perZar <= 0) return '';
  const each = 1 / perZar;
  return each >= 1 ? each.toFixed(2) : each.toFixed(4);
}

/**
 * The exchange rates, and the feed that keeps them.
 *
 * Since 21 September 2026 billing reads free public feeds every twelve hours and
 * writes the day's mid-market rate here, so nobody has to remember to. Three
 * figures follow from each: the mid-market rate itself, the rate a customer's
 * payment in the currency converts at, and the rate a cost the platform pays in
 * the currency (Meta's dollars) converts at. The last two are moved from
 * mid-market by the spread, each in the direction that cannot lose money.
 *
 * A figure can still be set by hand. With "Update automatically" off the feed
 * leaves that currency alone; left on, the next pass may replace the figure.
 */
export function FxRatesCard() {
  const t = useT();
  const { data, isLoading } = useFxRates();
  const mut = useUpdateFxRate();
  const refresh = useRefreshFxRates();
  const saveSettings = useUpdateFxSettings();
  const [drafts, setDrafts] = useState<Record<string, number | ''>>({});
  const [spread, setSpread] = useState<number | ''>('');
  const rows = (data?.data ?? []).filter((r) => r.currency !== 'ZAR');
  const meta = data?.meta;

  useEffect(() => { if (meta) setSpread(Number(meta.spread_percent)); }, [meta]);

  const dirty = rows.filter((r) => {
    const d = drafts[r.currency];
    return typeof d === 'number' && d > 0 && d !== Number(r.per_zar);
  });
  const spreadDirty = typeof spread === 'number' && meta !== undefined && spread !== Number(meta.spread_percent);
  const spreadInvalid = typeof spread === 'number' && (spread < 0 || spread > 25);

  async function saveAll() {
    let saved = 0;
    for (const r of dirty) {
      const v = drafts[r.currency];
      if (typeof v !== 'number') continue;
      try {
        await mut.mutateAsync({ currency: r.currency, change: { per_zar: v } });
        saved += 1;
        setDrafts((d) => { const n = { ...d }; delete n[r.currency]; return n; });
      } catch (e) {
        notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('billing.fx.title'));
        return;
      }
    }
    if (spreadDirty && !spreadInvalid && typeof spread === 'number') {
      try { await saveSettings.mutateAsync(spread); saved += 1; }
      catch (e) { notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('billing.fx.title')); return; }
    }
    if (saved > 0) notifySuccess(t('billing.fx.savedMany', { count: saved }), t('billing.fx.title'));
  }

  function refreshNow() {
    refresh.mutate(undefined, {
      onSuccess: (res) => {
        const changed = res.data.lines.filter((l) => l.changed).length;
        const held = res.data.lines.filter((l) => l.outcome === 'sources_disagree' || l.outcome === 'unconfirmed_jump');
        if (res.data.feeds_reached.length === 0) notifyError(t('billing.fx.refresh.noFeeds'), t('billing.fx.title'));
        else if (held.length > 0) notifyError(t('billing.fx.refresh.held', { currencies: held.map((l) => l.currency).join(', ') }), t('billing.fx.title'));
        else notifySuccess(t('billing.fx.refresh.done', { changed, feeds: res.data.feeds_reached.length }), t('billing.fx.title'));
        setDrafts({});
      },
      onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('billing.fx.title'))
    });
  }

  return (
    <Card withBorder>
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="wrap">
          <Stack gap="xs" style={{ minWidth: 0, flex: '1 1 320px' }}>
            <Group gap="sm" align="center">
              <Title order={5}>{t('billing.fx.title')}</Title>
              {meta && (meta.feed_enabled
                ? <Badge size="sm" variant="light" color={meta.stale ? 'orange' : 'green'}>{meta.stale ? t('billing.fx.feed.stale') : t('billing.fx.feed.on', { hours: meta.refresh_hours })}</Badge>
                : <Badge size="sm" variant="light" color="gray">{t('billing.fx.feed.off')}</Badge>)}
            </Group>
            <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('billing.fx.body')}</Text>
            {meta?.last_ok_at && (
              <Text role="caption" c="var(--rr-color-text-muted)">{t('billing.fx.lastOk', { when: formatDateTime(meta.last_ok_at) })}</Text>
            )}
          </Stack>
          <Group gap="sm">
            <Button size="xs" variant="default" loading={refresh.isPending} onClick={refreshNow}>{t('billing.fx.refresh')}</Button>
            <Button size="xs" variant="outline" disabled={(dirty.length === 0 && !spreadDirty) || spreadInvalid} loading={mut.isPending || saveSettings.isPending} onClick={() => void saveAll()}>
              {t('billing.fx.saveAll')}
            </Button>
          </Group>
        </Group>

        {meta?.last_error && (
          <Alert color="warning" title={t('billing.fx.held.title')}>{t('billing.fx.held.body', { detail: meta.last_error })}</Alert>
        )}
        {meta && meta.stale && !meta.last_error && (
          <Alert color="warning" title={t('billing.fx.stale.title')}>{t('billing.fx.stale.body')}</Alert>
        )}

        {isLoading && <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('common.loading')}</Text>}
        {rows.length > 0 && (
          <SimpleGrid cols={GRID_COLS} spacing="md">
            {rows.map((r) => (
              <Stack key={r.currency} gap="xs" className="rr-fx-rate">
                <NumberInput
                  size="xs"
                  label={t('billing.fx.perZar', { currency: r.currency })}
                  value={drafts[r.currency] ?? Number(r.per_zar)}
                  onChange={(v) => setDrafts((d) => ({ ...d, [r.currency]: typeof v === 'number' ? v : '' }))}
                  min={0}
                  step={0.001}
                  hideControls
                  description={t('billing.fx.each', {
                    currency: r.currency,
                    mid: randEach(Number(r.per_zar)),
                    customer: randEach(Number(r.customer_per_zar ?? r.per_zar)),
                    cost: randEach(Number(r.cost_per_zar ?? r.per_zar))
                  })}
                />
                <Text role="caption" c="var(--rr-color-text-muted)">
                  {r.source === 'manual'
                    ? t('billing.fx.source.manual', { when: formatDateTime(r.updated_at) })
                    : r.source && r.fetched_at
                      ? t('billing.fx.source.feed', { source: r.source, when: formatDateTime(r.fetched_at) })
                      : t('billing.fx.source.seed')}
                </Text>
                <Switch
                  size="xs"
                  label={t('billing.fx.auto')}
                  checked={r.auto_update ?? true}
                  disabled={mut.isPending}
                  onChange={(on) => mut.mutate({ currency: r.currency, change: { auto_update: on } })}
                />
              </Stack>
            ))}
          </SimpleGrid>
        )}

        <NumberInput
          size="xs"
          label={t('billing.fx.spread')}
          value={spread}
          onChange={(v) => setSpread(typeof v === 'number' ? v : '')}
          min={0}
          max={25}
          step={0.5}
          error={spreadInvalid ? t('billing.fx.spreadInvalid') : undefined}
          description={t('billing.fx.spreadHint')}
          style={{ maxWidth: 360 }}
        />

        <Text role="caption" c="var(--rr-color-text-muted)">
          {t('billing.fx.attribution.lead')}{' '}
          <a href="https://www.exchangerate-api.com" target="_blank" rel="noreferrer">{t('billing.fx.attribution.erApi')}</a>{', '}
          <a href="https://github.com/fawazahmed0/exchange-api" target="_blank" rel="noreferrer">{t('billing.fx.attribution.currencyApi')}</a>{' '}
          {t('billing.fx.attribution.and')}{' '}
          <a href="https://frankfurter.dev" target="_blank" rel="noreferrer">{t('billing.fx.attribution.frankfurter')}</a>
          {'.'}
        </Text>
      </Stack>
    </Card>
  );
}
